import gql from 'graphql-tag';
import {
  DocusignEnvelopeSchema,
  SignableAnnotationSchema,
  SignableFileSchema,
  SignableSchema,
  SignerSchema,
} from '../../definitions/schemas';
import {
  envelopeFields,
  signableFields,
  signableSignFields,
} from './SignableFields';
import { SignableProviders } from '../../enums/SignableProvider';
import Interface from '../Interface';

interface CreateSignableInput {
  annotations?: SignableAnnotationSchema[];
  files?: SignableFileSchema[];
  key?: string;
  name: string;
  provider: SignableProviders;
  providerDocumentId?: string;
  signableTemplateId?: string;
  signers?: SignerSchema[];
}

interface CreateSignableData {
  createSignable: {
    signable: SignableSchema;
  };
}

interface UpdateSignableInput {
  id: string;
  annotations?: SignableAnnotationSchema[];
  name?: string;
  signers?: SignerSchema[];
}

interface UpdateSignableData {
  updateSignable: {
    signable: SignableSchema;
  };
}

interface RefreshSignableInput {
  id: string;
}

interface RefreshSignableData {
  refreshSignable: {
    signable: SignableSchema;
  };
}

interface VoidSignableInput {
  id: string;
  notifySigners: boolean;
  reason?: string;
}

interface VoidSignableData {
  voidSignable: {
    signable: SignableSchema;
  };
}

interface GetSignableInput {
  id: string;
}

interface GetSignableData {
  getSignable: {
    signable: SignableSchema;
  };
}

interface AnnotationValueInput {
  id: string;
  value: string;
  width?: number;
}

interface SignableSignInput {
  id: string;
  annotations: AnnotationValueInput[];
}

interface SignableSignData {
  signableSign: {
    signable: SignableSchema;
  };
}

interface SearchDocusignEnvelopesData {
  getDocusignEnvelopes: DocusignEnvelopeSchema[];
}

interface GetDocusignSignableUrlInput {
  redirectUrl: string;
  roomUuid: string;
  signableId: string;
}

interface GetDocusignSignableUrlData {
  getDocusignSignableUrl: {
    url: string;
  };
}

export default class SignablesInterface extends Interface {
  public async create(input: CreateSignableInput) {
    return this.mutate<CreateSignableData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation createSignable($input: CreateSignableInput!) {
            createSignable(input: $input) {
              errors
              signable {
                ${signableFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error creating signable document');
  }

  public async update(input: UpdateSignableInput) {
    return this.mutate<UpdateSignableData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateSignable($input: UpdateSignableInput!) {
            updateSignable(input: $input) {
              errors
              signable {
                ${signableFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error update signable document');
  }

  public async refresh(input: RefreshSignableInput) {
    return this.mutate<RefreshSignableData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation refreshSignable(
            $input: RefreshSignableInput!
          ) {
            refreshSignable(input: $input) {
              errors
              signable {
                ${signableFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error syncing signable document');
  }

  public async void(input: VoidSignableInput) {
    return this.mutate<VoidSignableData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation voidSignable(
            $input: VoidSignableInput!
          ) {
            voidSignable(input: $input) {
              errors
              signable {
                ${signableFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error voiding signable document');
  }

  public async getSignable(input: GetSignableInput) {
    return this.mutate<GetSignableData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation getSignable(
            $input: GetSignableInput!
          ) {
            getSignable(input: $input) {
              errors
              signable {
                ${signableSignFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error getting signable document');
  }

  public async sign(input: SignableSignInput) {
    return this.mutate<SignableSignData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation signableSign(
            $input: SignableSignInput!
          ) {
            signableSign(input: $input) {
              errors
              signable {
                ${signableFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error signing document');
  }

  public async searchDocusignEnvelopes() {
    const response = await this.query<SearchDocusignEnvelopesData>(
      () =>
        this.apolloService.executeQuery<SearchDocusignEnvelopesData>({
          query: gql`
            query getDocusignEnvelopes {
              getDocusignEnvelopes {
                ${envelopeFields}
              }
            }
          `,
        }),
      'Error getting DocuSign envelopes'
    );

    return response.getDocusignEnvelopes;
  }

  public async getDocusignSignableUrl(variables: GetDocusignSignableUrlInput) {
    const response = await this.query<GetDocusignSignableUrlData>(
      () =>
        this.apolloService.executeQuery<GetDocusignSignableUrlData>({
          query: gql`
            query getDocusignSignableUrl(
              $roomUuid: ID!
              $redirectUrl: String!
              $signableId: ID!
            ) {
              getDocusignSignableUrl(
                roomUuid: $roomUuid
                redirectUrl: $redirectUrl
                signableId: $signableId
              ) {
                url
              }
            }
          `,
          variables,
        }),
      'Error getting DocuSign sign url'
    );

    return response.getDocusignSignableUrl;
  }
}
